import { useBilling } from "@/hooks/use-billing";

export interface Entitlements {
  /** Unlimited storage add-on (the free tier caps at 1 GB). */
  storageUnlimited: boolean;
  /** Frontier-models add-on (the old binary "Pro" gate). */
  modelsPremium: boolean;
  /** Still resolving the very first `/billing/status` fetch. */
  loading: boolean;
  /** Whether Stripe billing is configured for this deployment at all — false
   * in local/self-hosted mode, where there is no paywall to speak of. */
  billingEnabled: boolean;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
}

/** Derives the user's entitlements from {@link useBilling}. This is the ONLY
 * place that should interpret `storage_unlimited`/`models_premium` — every
 * gated surface (upload limits, model picker, `useEngine`'s `requires` check)
 * reads through here rather than re-deriving from raw billing status.
 *
 * Local/self-hosted mode (`billingEnabled: false`) has no paywall: both flags
 * report `true` so nothing in the studio renders an upgrade prompt it can't
 * act on. This is what keeps the engine model "DB/stateless-safe in local
 * mode" — no entitlement ever blocks an action when there's no billing to
 * begin with. */
export function useEntitlements(): Entitlements {
  const billing = useBilling();
  const data = billing.data;
  const billingEnabled = data?.enabled ?? false;

  if (!billingEnabled) {
    return {
      storageUnlimited: true,
      modelsPremium: true,
      loading: billing.isLoading,
      billingEnabled,
      status: "none",
      cancelAtPeriodEnd: false,
      currentPeriodEnd: null,
    };
  }

  return {
    storageUnlimited: data?.storage_unlimited ?? false,
    modelsPremium: data?.models_premium ?? false,
    loading: billing.isLoading,
    billingEnabled,
    status: data?.status ?? "none",
    cancelAtPeriodEnd: data?.cancel_at_period_end ?? false,
    currentPeriodEnd: data?.current_period_end ?? null,
  };
}
